import {
  CPE_CONNECTION_LABELS,
  CPE_CONNECTION_STATUSES,
  type AssetDetail,
  type CustomerListItem,
  type CustomerSummary,
  type Paginated,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip, StatusIndicator } from '@ftthnms/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { House, Plus, Search, Unplug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AssetDrawer } from '../components/AssetDrawer';
import { AssetForm } from '../components/AssetForm';
import { Pager } from '../components/common';
import { ExportMenu } from '../components/ExportMenu';
import { api, nf, qs } from '../lib/api';
import { toIndicator } from '../lib/monitoring';
import { useSession } from '../lib/session';

const CONNECTION_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  connected: 'success',
  disconnected: 'danger',
  suspended: 'warning',
  maintenance: 'neutral',
};

type Panel = { kind: 'view'; id: string } | { kind: 'edit'; asset: AssetDetail } | { kind: 'create' };

/** Pelanggan: CPE (ONT/HTB) with customer data and the ODP port feeding them. Search by name, SN, ID, phone. */
export function CustomersPage() {
  const { area, ability } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [connection, setConnection] = useState('');
  const [unlinked, setUnlinked] = useState(false);
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<Panel | null>(null);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, connection, unlinked, area]);

  const summary = useQuery({
    queryKey: ['customers', 'summary', area],
    queryFn: () => api<CustomerSummary>(`/api/v1/customers/summary${qs({ area })}`),
  });
  const list = useQuery({
    queryKey: ['customers', 'list', { area, q: debouncedQ, connection, unlinked, page }],
    queryFn: () =>
      api<Paginated<CustomerListItem>>(
        `/api/v1/customers${qs({ area, q: debouncedQ, connection, unlinked: unlinked ? '1' : undefined, page, pageSize })}`,
      ),
    placeholderData: keepPreviousData,
  });
  const s = summary.data;
  const data = list.data;
  const filtered = !!(debouncedQ || connection || unlinked);

  const kpis: Array<{ label: string; value: number; tone: string; onClick?: () => void; active?: boolean }> =
    [
      {
        label: 'Total pelanggan',
        value: s?.total ?? 0,
        tone: 'primary',
        onClick: () => (setConnection(''), setUnlinked(false)),
        active: !connection && !unlinked,
      },
      {
        label: 'Terhubung',
        value: s?.byConnection.connected ?? 0,
        tone: 'success',
        onClick: () => (setConnection('connected'), setUnlinked(false)),
        active: connection === 'connected',
      },
      {
        label: 'Terputus / diisolir',
        value: (s?.byConnection.disconnected ?? 0) + (s?.byConnection.suspended ?? 0),
        tone: 'danger',
        onClick: () => (setConnection('disconnected'), setUnlinked(false)),
        active: connection === 'disconnected',
      },
      {
        label: 'Belum terhubung ke ODP',
        value: s?.unlinked ?? 0,
        tone: 'warning',
        onClick: () => (setConnection(''), setUnlinked(true)),
        active: unlinked,
      },
    ];

  return (
    <div className="fn-page-body">
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {kpis.map((k) => (
          <button
            key={k.label}
            type="button"
            className="fn-card fn-kpi fn-card--interactive"
            aria-pressed={k.active}
            onClick={k.onClick}
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              outline: k.active ? '2px solid var(--primary)' : undefined,
            }}
          >
            <div className="fn-kpi__head">
              <span className="fn-kpi__label">{k.label}</span>
              <span className={`fn-tile fn-tile--${k.tone}`}>
                {k.tone === 'warning' ? (
                  <Unplug className="i" aria-hidden />
                ) : (
                  <House className="i" aria-hidden />
                )}
              </span>
            </div>
            <div className="fn-kpi__value">
              <span className="fn-kpi__number">{summary.isPending ? '–' : nf.format(k.value)}</span>
              <span className="fn-kpi__unit">pelanggan</span>
            </div>
          </button>
        ))}
      </div>

      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-toolbar">
          <div className="fn-input-group" style={{ flex: 1, maxWidth: 340, minWidth: 180 }}>
            <Search className="i" aria-hidden />
            <input
              className="fn-input"
              aria-label="Cari pelanggan"
              placeholder="Cari nama, ID, SN ONT, telepon, alamat, ODP…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="fn-input"
            aria-label="Status koneksi"
            style={{ width: 'auto' }}
            value={connection}
            onChange={(e) => setConnection(e.target.value)}
          >
            <option value="">Semua status koneksi</option>
            {CPE_CONNECTION_STATUSES.map((c) => (
              <option key={c} value={c}>
                {CPE_CONNECTION_LABELS[c]}
              </option>
            ))}
          </select>
          <span className="fn-toolbar__spacer" />
          {ability.can('export', 'Asset') && <ExportMenu entity="customers" params={{ area }} />}
          {ability.can('create', 'Asset') && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus className="i" aria-hidden />}
              onClick={() => setPanel({ kind: 'create' })}
            >
              Tambah pelanggan
            </Button>
          )}
        </div>

        {data && data.total === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState
              icon={<House className="i i--xl" aria-hidden />}
              title={filtered ? 'Tidak ada pelanggan yang cocok' : 'Belum ada pelanggan'}
              text={
                filtered
                  ? undefined
                  : 'Pelanggan adalah aset CPE (ONT/HTB). Tambah di sini, di peta, atau lewat import KMZ.'
              }
            />
          </div>
        ) : (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>
                  <th>Pelanggan</th>
                  <th>Perangkat</th>
                  <th>ODP / port</th>
                  <th>Koneksi</th>
                  <th>Status perangkat</th>
                  <th>Paket</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setPanel({ kind: 'view', id: c.id })}
                    style={{ cursor: 'pointer' }}
                    aria-selected={panel?.kind === 'view' && panel.id === c.id}
                  >
                    <td>
                      <div className="fn-asset__name">{c.customerName ?? c.name}</div>
                      <div className="fn-asset__code">
                        {[c.customerRef, c.phone].filter(Boolean).join(' · ') || c.code}
                        {c.areaCode && (
                          <span style={{ marginLeft: 6 }}>
                            <RegionChip code={c.areaCode} />
                          </span>
                        )}
                      </div>
                      {c.address && (
                        <div
                          className="fn-hint"
                          style={{
                            maxWidth: 320,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.address}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="mono">{c.code}</div>
                      <div className="fn-hint">
                        {c.cpeKind ?? 'CPE'}
                        {c.serialNumber ? ` · SN ${c.serialNumber}` : ''}
                      </div>
                    </td>
                    <td>
                      {c.odp ? (
                        <>
                          <div className="mono">{c.odp.code}</div>
                          <div className="fn-hint">port {c.odp.port}</div>
                        </>
                      ) : (
                        <Badge tone="warning">Belum terhubung</Badge>
                      )}
                    </td>
                    <td>
                      <Badge tone={CONNECTION_TONE[c.connection ?? 'connected'] ?? 'neutral'} dot>
                        {CPE_CONNECTION_LABELS[
                          (c.connection ?? 'connected') as keyof typeof CPE_CONNECTION_LABELS
                        ] ?? c.connection}
                      </Badge>
                    </td>
                    <td>
                      {c.mon ? (
                        <StatusIndicator status={toIndicator(c.mon)} />
                      ) : (
                        <span className="fn-hint">tidak dipantau</span>
                      )}
                    </td>
                    <td>{c.servicePlan ?? <span className="fn-hint">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={data?.total ?? 0} unit="pelanggan" page={page} pageSize={pageSize} onPage={setPage} />
      </section>

      {panel?.kind === 'view' && (
        <AssetDrawer
          key={panel.id}
          id={panel.id}
          onClose={() => setPanel(null)}
          onEdit={(asset) => setPanel({ kind: 'edit', asset })}
          onOpenAsset={(id) => setPanel({ kind: 'view', id })}
          onFocus={(asset) => void navigate({ to: '/map', search: { focus: asset.id } })}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <AssetForm
          key={`edit-${panel.asset.id}`}
          mode="edit"
          asset={panel.asset}
          onClose={() => setPanel({ kind: 'view', id: panel.asset.id })}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
      {panel?.kind === 'create' && (
        <AssetForm
          mode="create"
          initialType="CPE"
          onClose={() => setPanel(null)}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
    </div>
  );
}
